import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import QRCodeStyling from "qr-code-styling";
import {
  Button,
  Container,
  Divider,
  Message,
  Modal,
  Popup,
  Table,
} from "semantic-ui-react";
import { useTheme } from "styled-components";

import { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";

import api from "api";
import AppIcon from "src/images/appIcon.png";
import { useAppSelector } from "store";
import util from "utils/utils";

type ApiToken = NonNullable<
  NonNullable<
    NonNullable<OpenAPI.Schemas["User"]["services"]>["api"]
  >["externalTokens"]
>[number];

type ApiTokenProps = {
  token: ApiToken;
  tokenIndex: number;
  deleteToken: (tokenId: string) => void;
  onViewToken: (tokenId: string, tokenIndex: number) => void;
};

const ApiToken = ({
  token,
  tokenIndex,
  deleteToken,
  onViewToken,
}: ApiTokenProps) => (
  <Table.Row>
    <Table.Cell collapsing>{token.serviceName || "Unknown"}</Table.Cell>
    <Table.Cell>
      <Popup
        content={
          token.userHasViewedToken ?
            "You have already viewed this token. It is no longer visible for security reasons. If you have the lost the token information you will need to delete it and recreate a token for the relevant service"
          : "You are able to view this token once. Press the 'View token' button to view the token. It will no longer be visible after this."
        }
        trigger={
          <Input
            fluid
            value={"x".repeat(20)}
            size="small"
            readOnly
            type="password"
          />
        }
      />
    </Table.Cell>
    <Table.Cell collapsing>
      {!token.userHasViewedToken ?
        <Button
          size="mini"
          primary
          onClick={() => onViewToken(token.id, tokenIndex)}
          icon="eye"
          content="View token"
        />
      : null}
      <Button
        size="mini"
        icon="trash"
        basic
        onClick={() => deleteToken(token.token)}
      />
    </Table.Cell>
  </Table.Row>
);

function ApiSettings() {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const [newApiTokenService, setNewApiTokenService] = useState("");
  const [creatingToken, setCreatingToken] = useState(false);
  const [externalTokens, setExternalTokens] = useState<ApiToken[]>([]);
  const [viewingExternalToken, setViewingExternalToken] =
    useState<ApiToken | null>(null);
  const copyToken = () => {
    navigator.clipboard
      .writeText(viewingExternalToken?.token || "")
      .then(() => {
        toast("Token copied");
      })
      .catch(() => {
        toast("Could not copy token");
      });
  };

  const [generatingQRCode, setGeneratingQRCode] = useState(false);
  const [QRCodeValue, setQRCodeValue] = useState("");
  const [localIp, setLocalIp] = useState("");
  const isDevMode = import.meta.env.VITE_STAGE === "dev";
  const theme = useTheme();
  const otpQrRef = useRef(null);

  useEffect(() => {
    api.auth.getApiTokens(
      (newTokens) => {
        setExternalTokens(newTokens.externalTokens);
      },
      () => {},
    );
  }, []);

  const createToken = () => {
    setCreatingToken(true);
    api.auth.createExternalApiToken(
      { serviceName: newApiTokenService },
      (token) => {
        setCreatingToken(false);
        setExternalTokens([...externalTokens, token]);
        setViewingExternalToken(token);
        setNewApiTokenService("");
        toast("Your new token is ready to be used");
      },
      (err) => {
        toast.error(err.message);
        setCreatingToken(false);
      },
    );
  };

  const viewToken = useCallback((tokenId: string, tokenIndex: number) => {
    util
      .confirm(
        "View API token",
        "This will mark the token as viewed, and it will no longer be visible to you. Please note down the token to continue using it. Are you sure you want to continue?",
      )
      .then(() => {
        api.auth.viewExternalApiToken(
          tokenId,
          tokenIndex,
          ({ success, token: viewingToken }) => {
            setExternalTokens((prevTokens) =>
              prevTokens.map((token, index) => {
                if (index === tokenIndex) {
                  return { ...token, userHasViewedToken: true };
                }
                return token;
              }),
            );
            if (!success) {
              toast.error("You have already viewed this token");
            } else {
              if (!viewingToken) {
                toast.error("Could not show token");
                return;
              }
              setViewingExternalToken(viewingToken);
            }
          },
          (err) => {
            toast.error(err.message);
          },
        );
      })
      .catch(() => {});
  }, []);

  const deleteToken = (tokenId: string, tokenIndex: number) => {
    util
      .confirm(
        "Delete this token?",
        `Any services using this token will no longer be able to access your ${util.appName()} account.`,
      )
      .then(() => {
        setCreatingToken(true);
        api.auth.deleteExternalApiToken(
          tokenId,
          tokenIndex,
          () => {
            setCreatingToken(false);
            if (tokenId) {
              setExternalTokens(
                externalTokens.filter((token) => tokenId !== token.id),
              );
            } else {
              setExternalTokens(
                externalTokens.filter((_, index) => index !== tokenIndex),
              );
            }
          },
          (err) => {
            setCreatingToken(false);
            toast.error(err.message);
          },
        );
      })
      .catch(() => {});
  };

  const generateQRCode = useCallback(() => {
    setGeneratingQRCode(true);
    api.auth.generateExchangeToken(
      ({ exchangeToken }) => {
        setQRCodeValue(
          `${isDevMode ? `exp://${localIp}:19000/--/` : "simplydo://"}?org=${user.ownerOrganisation.code}&token=${exchangeToken}`,
        );
        setGeneratingQRCode(false);
      },
      (err) => {
        toast.error(err.message);
        setGeneratingQRCode(false);
      },
    );
  }, [isDevMode, user, localIp]);

  const qrCode = useMemo(
    () =>
      new QRCodeStyling({
        width: 200,
        height: 200,
        type: "svg",
        data: QRCodeValue,
        image: AppIcon,
        dotsOptions: { color: "#14435B" },
        backgroundOptions: { color: "#ffffff" },
        imageOptions: { margin: 4 },
      }),
    [QRCodeValue],
  );

  useEffect(() => {
    if (QRCodeValue && !qrCode._container && otpQrRef.current) {
      qrCode.append(otpQrRef.current);
    }
  }, [QRCodeValue, qrCode]);

  return (
    <Container>
      <h3>Mobile App Login</h3>
      <p>
        Scan this QR code to automatically open the mobile app and authenticate
        using this account ({user.profile.fullName}). For security reasons the
        code is single-use only, it is required to generate a new code for every
        login attempt.
      </p>
      {QRCodeValue ?
        <div>
          <div ref={otpQrRef} />
          <Input
            fluid
            size="small"
            readOnly
            value={QRCodeValue}
            style={{ margin: "10px 0" }}
            action={
              !theme.sizes.isComputer ?
                <Button
                  onClick={() => {
                    window.location.href = QRCodeValue;
                  }}
                >
                  Open QR link
                </Button>
              : null
            }
          />
        </div>
      : <Message
          warning
          icon="warning sign"
          header="Before you proceed"
          content="This QR code is used to identify you directly with our service. It should be considered a private and secure credential. Just like a password, never share this code with anyone else."
        />
      }
      {isDevMode ?
        <Input
          size="small"
          style={{ margin: "10px 0", maxWidth: 300 }}
          value={localIp}
          onChange={(_e, { value }) => setLocalIp(value)}
          placeholder="Enter local IP to open Expo"
        />
      : null}
      <Button
        primary
        onClick={generateQRCode}
        style={{ margin: "10px 0", maxWidth: "fit-content" }}
        loading={generatingQRCode}
        disabled={isDevMode && !localIp}
      >
        I understand, generate a login code
      </Button>

      <Divider section />
      <h3>{t("users.account.options.apiTokens.title")}</h3>
      <p>
        {t("users.account.options.apiTokens.info", { appName: util.appName() })}
      </p>

      <h5>{t("users.account.options.apiTokens.new")}</h5>
      <p>{t("users.account.options.apiTokens.newInfo")}</p>
      <Input
        size="small"
        fluid
        value={newApiTokenService}
        action={<Button primary content="Create" onClick={createToken} />}
        loading={creatingToken}
        placeholder='Service name (e.g. "Zapier")'
        onChange={(e) => setNewApiTokenService(e.target.value)}
      />

      <h5>Tokens you&apos;ve already created</h5>
      <p>
        To authenticate using these tokens, call the appropriate endpoint over{" "}
        <code>HTTPS</code>, placing the token in the <code>Authorization</code>{" "}
        header. For more information about using the {util.appName()} API please
        get in touch.
      </p>
      {externalTokens.length > 0 ?
        <Table basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Service</Table.HeaderCell>
              <Table.HeaderCell>Token</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {externalTokens.map((token, tokenIndex) => (
              <ApiToken
                key={token.id}
                token={token}
                deleteToken={() => deleteToken(token.id, tokenIndex)}
                tokenIndex={tokenIndex}
                onViewToken={viewToken}
              />
            ))}
          </Table.Body>
        </Table>
      : <Message>No tokens have been created yet.</Message>}

      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={!!viewingExternalToken}
        onClose={() => setViewingExternalToken(null)}
        closeOnDimmerClick={false}
      >
        <Modal.Header>Viewing external API token</Modal.Header>
        <Modal.Content>
          <Message warning>
            <Message.Header>Important!</Message.Header>
            <p>
              This token is only shown once. Please copy it now and keep it
              safe. Once you close this modal, you cannot view this token again.
            </p>
          </Message>
          <Input
            fluid
            value={viewingExternalToken?.token}
            size="small"
            readOnly
            action={<Button size="mini" onClick={copyToken} icon="copy" />}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={() => setViewingExternalToken(null)}>
            Done
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
}

export default ApiSettings;
